import {
  forkJoin, from, concat, fromEvent,
} from 'rxjs';
import { combineEpics, ofType } from 'redux-observable';
import {
  catchError, mergeMap, take, takeUntil, startWith, ignoreElements, mapTo, switchMap,
  debounceTime, tap,
} from 'rxjs/operators';
import api from 'api/index';
import { handleError } from 'base/errors/store/actions';
import blobToArrayBuffer from 'documents/util/blobToArrayBuffer';
import { closeModal } from 'modal/store/actions';
import toast from 'base/components/Toast/toast';
import { isSafari } from 'utils/browser';
import * as actionTypes from './actionTypes';
import {
  cacheDocumentOffline,
  cacheDocumentOfflineSuccess,
  uncacheDocumentOfflineSuccess,
  cacheDocumentsOfflineSuccess, // eslint-disable-line
  cacheDocumentOfflinePasswordIncorrect,
  setIsOffline,
  connectionChange,
} from './actions';
import OfflineFileStore from '../util/OfflineFileStore';
import OfflineAnnotationStore from '../util/OfflineAnnotationStore';

const initialConnectionStatusEpic = () =>
  fromEvent(window, 'load').pipe(
    switchMap(() =>
      (typeof navigator.onLine === 'boolean' ? [setIsOffline(!navigator.onLine)] : [])),
  );

const onlineEventEpic = () =>
  fromEvent(window, 'online').pipe(
    switchMap(() => [connectionChange(false)]),
  );

const offlineEventEpic = () =>
  fromEvent(window, 'offline').pipe(
    switchMap(() => [connectionChange(true)]),
  );

const connectionChangeEpic = (action$) => action$.pipe(
  ofType(actionTypes.CONNECTION_CHANGE),
  // Debounce to avoid switching forth and back if there is only a temporary interruption
  debounceTime(500),
  switchMap((action) => [setIsOffline(action.isOffline)]),
);

const cacheDocumentEpic = (action$) => action$.pipe(
  ofType(actionTypes.CACHE_DOCUMENT),
  mergeMap((action) => forkJoin([
    api.documents.downloadPdfVersion(action.document)
      .pipe(
        mergeMap((response) => from(blobToArrayBuffer(response.body))),
        mergeMap((buffer) => {
          const arrayBuffer = buffer.target.result;
          const storePromise = OfflineFileStore.set(action.document.id, arrayBuffer);
          return from(storePromise);
        }),
      ),
    api.documents.fetchAnnotations(action.document.id, action.password)
      .pipe(mergeMap((response) => {
        const storePromise = OfflineAnnotationStore.set(action.document.id, response.body);
        return from(storePromise);
      })),
  ]).pipe(
    mergeMap(() => [cacheDocumentOfflineSuccess(action.document)]),
    catchError((error) => (error.status && error.status === 400
      ? [cacheDocumentOfflinePasswordIncorrect(action.document)]
      : [handleError(error)])),
  )),
);

/**
 * Caches multiple documents offline
 * Documents are not cached in parallel, but one after another.
 * This is an easy way to prevent timing related bugs when
 * the password of multiple documents is incorrect.
 * As soon as a password is incorrect, this epic is stopped
 */
const cacheDocumentsEpic = (action$) => action$.pipe(
  ofType(actionTypes.CACHE_DOCUMENTS),
  mergeMap((action) => concat(
    ...action.documents.map((document) => action$.pipe(
      ofType(actionTypes.CACHE_DOCUMENT_SUCCESS, actionTypes.CACHE_DOCUMENT_PASSWORD_INCORRECT),
      take(1),
      ignoreElements(),
      startWith(cacheDocumentOffline(document, action.password)),
    )),
    action$.pipe(
      ofType(actionTypes.CACHE_DOCUMENT_SUCCESS),
      take(1),
      startWith(cacheDocumentsOfflineSuccess(action.documents)),
    ),
  ).pipe(takeUntil(action$.pipe(ofType(actionTypes.CACHE_DOCUMENT_PASSWORD_INCORRECT))))),
);

const cacheDocumentsSuccessEpic = (action$) => action$.pipe(
  ofType(actionTypes.CACHE_DOCUMENTS_SUCCESS),
  tap(() => {
    if (isSafari()) {
      toast.warn('Safari will only keep your offline documents \n'
          + 'for the next 7 days for data privacy reasons.');
    }
  }),
  mapTo(closeModal('DOCUMENT_CACHE_OFFLINE')),
);

const uncacheDocumentEpic = (action$) => action$.pipe(
  ofType(actionTypes.UNCACHE_DOCUMENT),
  mergeMap((action) => {
    OfflineFileStore.delete(action.document.id.toString());
    OfflineAnnotationStore.delete(action.document.id.toString());
    return [uncacheDocumentOfflineSuccess(action.document)];
  }),
);


export default combineEpics(
  initialConnectionStatusEpic,
  connectionChangeEpic,
  onlineEventEpic,
  offlineEventEpic,
  cacheDocumentEpic,
  cacheDocumentsEpic,
  cacheDocumentsSuccessEpic,
  uncacheDocumentEpic,
);
